import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DeleteIcon from '@mui/icons-material/Delete';

interface PaymentTermsSettingsProps {
  value: number[];
  onChange: (next: number[]) => void;
}

type AddError = 'invalid' | 'duplicate' | null;

/**
 * Workshop payment-term list (#1997). Lives on the settings page rather than
 * inside `WorkshopSettingsForm` so it can ship without widening that form's
 * type; it still rides the same PATCH `/tenants/me/settings` save.
 */
export function PaymentTermsSettings({ value, onChange }: PaymentTermsSettingsProps) {
  const { t } = useTranslation('settings');
  const { t: tc } = useTranslation('common');
  const [draft, setDraft] = useState('');
  const [addError, setAddError] = useState<AddError>(null);

  const move = (index: number, delta: number) => {
    const target = index + delta;
    if (target < 0 || target >= value.length) return;
    const next = [...value];
    const [item] = next.splice(index, 1);
    if (item === undefined) return;
    next.splice(target, 0, item);
    onChange(next);
  };

  const removeAt = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
  };

  const addTerm = () => {
    const trimmed = draft.trim();
    const parsed = Number(trimmed);
    if (!/^[1-9]\d*$/.test(trimmed) || parsed > 365) {
      setAddError('invalid');
      return;
    }
    if (value.includes(parsed)) {
      setAddError('duplicate');
      return;
    }
    onChange([...value, parsed]);
    setDraft('');
    setAddError(null);
  };

  const addErrorText =
    addError === 'invalid'
      ? t('paymentTerms.invalid')
      : addError === 'duplicate'
        ? t('paymentTerms.duplicate')
        : undefined;

  return (
    <Box>
      <Typography variant="h6" fontWeight={600} gutterBottom>
        {t('paymentTerms.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('paymentTerms.description')}
      </Typography>
      <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'divider', mb: 4 }}>
        <Stack spacing={1.5}>
          {value.length === 0 && (
            <Alert severity="error">{t('paymentTerms.emptyError')}</Alert>
          )}
          {value.map((days, index) => (
            <Box
              key={`${days}-${index}`}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                py: 0.5,
              }}
            >
              <Typography sx={{ flex: 1 }}>
                {t('paymentTerms.days', { count: days })}
              </Typography>
              <Tooltip title={t('paymentTerms.moveUp')}>
                <span>
                  <IconButton
                    size="small"
                    onClick={() => move(index, -1)}
                    disabled={index === 0}
                    aria-label={t('paymentTerms.moveUp')}
                    sx={{ minWidth: 44, minHeight: 44 }}
                  >
                    <ArrowUpwardIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={t('paymentTerms.moveDown')}>
                <span>
                  <IconButton
                    size="small"
                    onClick={() => move(index, 1)}
                    disabled={index === value.length - 1}
                    aria-label={t('paymentTerms.moveDown')}
                    sx={{ minWidth: 44, minHeight: 44 }}
                  >
                    <ArrowDownwardIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={t('paymentTerms.removeAria', { days })}>
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => removeAt(index)}
                  aria-label={t('paymentTerms.removeAria', { days })}
                  sx={{ minWidth: 44, minHeight: 44 }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          ))}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, pt: 1 }}>
            <TextField
              size="small"
              type="number"
              label={t('paymentTerms.addLabel')}
              value={draft}
              onChange={(e) => {
                setDraft(e.target.value);
                setAddError(null);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addTerm();
                }
              }}
              error={addError !== null}
              helperText={addErrorText ?? ''}
              sx={{ maxWidth: 240 }}
              slotProps={{
                htmlInput: { min: 1, max: 365, step: 1 },
              }}
            />
            <Button
              type="button"
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={addTerm}
              sx={{ mt: 0.5 }}
            >
              {tc('add')}
            </Button>
          </Box>
        </Stack>
      </Paper>
    </Box>
  );
}
